import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api, { trackActivity, marketDataAPI } from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, LogarithmicScale, PointElement, LineElement,
  BarElement, LineController, BarController, ScatterController,
  Filler, Title, Tooltip as ChartTooltip, Legend as ChartLegend
} from 'chart.js';
import { Chart as ChartCanvas } from 'react-chartjs-2';
import {
  MARKET_DATA_COLORS, TRANSFORMS,
  applyTransform, getTransformLabel, makeMarketDataChartOptions,
  makeTickFormatter, getTransformsForFreq,
} from '../utils/marketDataTransforms';
import {
  DEFAULT_SOURCE_TEXT, MONTH_CUM_DAYS,
  computeDerived, computeHistogram, olsFit, resampleSeries, resolveDateRange,
  seasonalSplit, upgradeChartConfig,
} from '../utils/chartConfigSchema';
import {
  annotationsPlugin, lastValueLabelsPlugin, sdBandsPlugin, zeroLinePlugin,
} from '../utils/chartPlugins';
import './MarketDataApp.css';
import ManagedDisclaimer from '../components/ManagedDisclaimer';

// The generic <Chart> component (needed for mixed bar/line, scatter and
// histogram charts) does not auto-register controllers the way the typed
// <Line> component does — LineController/BarController/ScatterController must
// be registered explicitly or Chart.js throws at runtime.
ChartJS.register(
  CategoryScale, LinearScale, LogarithmicScale, PointElement, LineElement,
  BarElement, LineController, BarController, ScatterController,
  Filler, Title, ChartTooltip, ChartLegend
);

const BORDER_DASH = { solid: [], dashed: [6, 4], dotted: [2, 3] };
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CHART_MODES = [
  { value: 'timeseries', label: 'Time series' },
  { value: 'histogram', label: 'Histogram' },
  { value: 'scatter', label: 'Scatter' },
  { value: 'seasonal', label: 'Seasonal' },
];
const RANGE_PILLS = [
  { value: 'all', label: 'Max' },
  { value: '1y', label: '1Y' }, { value: '3y', label: '3Y' },
  { value: '5y', label: '5Y' }, { value: '10y', label: '10Y' },
  { value: 'ytd', label: 'YTD' },
  { value: 'fixed', label: 'Fixed' },
];
const DERIVED_OPS = [
  { value: 'ratio', label: 'Ratio (A ÷ B)' },
  { value: 'spread', label: 'Spread (A − B)' },
  { value: 'scale', label: 'Scale (A × k)' },
  { value: 'corr', label: 'Rolling correlation (A, B)' },
];

const defaultAxisState = () => ({
  invert: false, log: false, min: '', max: '', decimals: '',
  pctSuffix: false, thousands: false,
});

const defaultSeriesSettings = (colour) => ({
  axis: 'y', transform: 'none', window: 20, anchor: '',
  colour, type: 'line', lineWidth: 1.5, lineStyle: 'solid',
  band: null, resample: null,
});

// UI axis state ('' = auto) → schema v2 axis config.
const toAxisV2 = (ax) => ({
  invert: !!ax.invert,
  log: !!ax.log,
  min: ax.min === '' || ax.min === null ? null : Number(ax.min),
  max: ax.max === '' || ax.max === null ? null : Number(ax.max),
  decimals: ax.decimals === '' || ax.decimals === null ? null : parseInt(ax.decimals, 10),
  pct_suffix: !!ax.pctSuffix,
  thousands: !!ax.thousands,
});

const fromAxisV2 = (ax) => ({
  invert: !!ax?.invert,
  log: !!ax?.log,
  min: ax?.min === null || ax?.min === undefined ? '' : String(ax.min),
  max: ax?.max === null || ax?.max === undefined ? '' : String(ax.max),
  decimals: ax?.decimals === null || ax?.decimals === undefined ? '' : String(ax.decimals),
  pctSuffix: !!ax?.pct_suffix,
  thousands: !!ax?.thousands,
});

function MarketDataApp() {
  const [availableTickers, setAvailableTickers] = useState(null);
  const [tickerLabels, setTickerLabels] = useState({});
  const [selectedClassification, setSelectedClassification] = useState(null);
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [tickerSearch, setTickerSearch] = useState('');
  const [chartData, setChartData] = useState({ dates: [], series: {} });
  const [loading, setLoading] = useState(false);
  const [tickerSettings, setTickerSettings] = useState({});
  const [axisConfig, setAxisConfig] = useState({
    y: defaultAxisState(),
    y1: defaultAxisState(),
  });
  const [regionFilter, setRegionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [styleFilter, setStyleFilter] = useState('');

  // Date range: mode + trailing preset / fixed dates, with the zoom sliders
  // as a percentage refinement inside the resolved window.
  const [rangeMode, setRangeMode] = useState('all');
  const [trailingPreset, setTrailingPreset] = useState('5y');
  const [fixedStart, setFixedStart] = useState('');
  const [fixedEnd, setFixedEnd] = useState('');
  const [showZoom, setShowZoom] = useState(false);
  const [xStart, setXStart] = useState(0);
  const [xEnd, setXEnd] = useState(100);

  // Chart mode + mode settings.
  const [chartMode, setChartMode] = useState('timeseries');
  const [stacked, setStacked] = useState(false);
  const [histTicker, setHistTicker] = useState('');
  const [histBins, setHistBins] = useState(30);
  const [scatterX, setScatterX] = useState('');
  const [trendLine, setTrendLine] = useState(true);
  const [recencyFade, setRecencyFade] = useState(false);
  const [seasonalTicker, setSeasonalTicker] = useState('');
  const [seasonalYears, setSeasonalYears] = useState(5);

  // Style options (mirrored in the server renderer's `options`).
  const [gridlines, setGridlines] = useState(false);
  const [zeroLine, setZeroLine] = useState(false);
  const [lastValues, setLastValues] = useState(false);
  const [legendShow, setLegendShow] = useState(true);
  const [legendPosition, setLegendPosition] = useState('bottom');
  const [sourceText, setSourceText] = useState(DEFAULT_SOURCE_TEXT);
  const [footnote, setFootnote] = useState('');
  const [showStylePanel, setShowStylePanel] = useState(false);

  // Annotations.
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [recessionBands, setRecessionBands] = useState(false);
  const [annBands, setAnnBands] = useState([]);
  const [annVlines, setAnnVlines] = useState([]);
  const [annHlines, setAnnHlines] = useState([]);

  // Derived expression series ("=<id>" pseudo-tickers).
  const [derived, setDerived] = useState([]);
  const [derivedDraft, setDerivedDraft] = useState({ op: 'ratio', a: '', b: '', k: 100, window: 60 });

  const chartRef = useRef(null);

  // Saved chart configs (server-rendered to house-style PNGs for decks).
  // Drummond-internal: flips false when the backend 403s the list call.
  const [savedChartsEnabled, setSavedChartsEnabled] = useState(true);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [showSavedPanel, setShowSavedPanel] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [saveFavourite, setSaveFavourite] = useState(false);
  const [chartTitle, setChartTitle] = useState('');
  const [chartSubtitle, setChartSubtitle] = useState('');
  const [savingConfig, setSavingConfig] = useState(false);
  const [loadedConfig, setLoadedConfig] = useState(null);
  const [configError, setConfigError] = useState('');
  const [configNotice, setConfigNotice] = useState('');

  // Fetch available tickers on mount
  useEffect(() => {
    trackActivity('app_access', 'market_data');
    api.get('/api/tactical/available-tickers')
      .then(res => {
        setAvailableTickers(res.data);
        const meta = res.data?.ticker_metadata || {};
        const labels = {};
        Object.entries(meta).forEach(([ticker, m]) => {
          if (m.label) labels[ticker] = m.label;
        });
        setTickerLabels(labels);
      })
      .catch(err => console.error('Error loading tickers:', err));
  }, []);

  const refreshSavedConfigs = useCallback(() => {
    marketDataAPI.listChartConfigs()
      .then(setSavedConfigs)
      .catch(err => {
        // Saved charts are Drummond-internal; client users get 403 and the
        // whole save/load UI stays hidden.
        if (err.response?.status === 403) setSavedChartsEnabled(false);
        else console.error('Error loading saved charts:', err);
      });
  }, []);

  useEffect(() => { refreshSavedConfigs(); }, [refreshSavedConfigs]);

  const getTickerLabel = useCallback((rawTicker) => {
    if (!rawTicker) return '-';
    return tickerLabels[rawTicker] || rawTicker;
  }, [tickerLabels]);

  const metadata = availableTickers?.ticker_metadata || {};
  const dailySet = useMemo(() => new Set(availableTickers?.daily_tickers || []), [availableTickers]);
  const monthlySet = useMemo(() => new Set(availableTickers?.monthly_tickers || []), [availableTickers]);

  const allTickers = useMemo(() => {
    if (!availableTickers) return [];
    return [...new Set([...dailySet, ...monthlySet])].sort();
  }, [availableTickers, dailySet, monthlySet]);

  const classificationTabs = useMemo(() => {
    if (!availableTickers) return [];
    const allSet = new Set([...dailySet, ...monthlySet]);
    const groups = {};
    allSet.forEach(t => {
      const cls = metadata[t]?.asset_class || 'Unclassified';
      if (!groups[cls]) groups[cls] = [];
      groups[cls].push(t);
    });
    const sorted = Object.keys(groups).sort((a, b) => {
      if (a === 'Unclassified') return 1;
      if (b === 'Unclassified') return -1;
      return a.localeCompare(b);
    });
    return [
      { name: 'All', tickers: [...allSet] },
      ...sorted.map(name => ({ name, tickers: groups[name] })),
    ];
  }, [availableTickers, metadata, dailySet, monthlySet]);

  useEffect(() => {
    if (selectedClassification === null && classificationTabs.length > 0) {
      setSelectedClassification('All');
    }
  }, [classificationTabs, selectedClassification]);

  const tabTickers = useMemo(() => {
    if (!availableTickers || !selectedClassification) return [];
    if (selectedClassification === 'All') return allTickers;
    const tab = classificationTabs.find(t => t.name === selectedClassification);
    return tab ? tab.tickers : [];
  }, [availableTickers, selectedClassification, classificationTabs, allTickers]);

  const marketFilterOptions = useMemo(() => {
    const regions = new Set(), types = new Set(), styles = new Set();
    tabTickers.forEach(t => {
      if (metadata[t]?.region) regions.add(metadata[t].region);
      if (metadata[t]?.type) types.add(metadata[t].type);
      if (metadata[t]?.style) styles.add(metadata[t].style);
    });
    return {
      regions: [...regions].sort(),
      types: [...types].sort(),
      styles: [...styles].sort(),
    };
  }, [tabTickers, metadata]);

  // Search and dropdown filters both scope to the selected tab; the list is
  // sorted alphabetically by display label.
  const filteredTickers = useMemo(() => {
    let list = tabTickers;
    if (regionFilter) list = list.filter(t => (metadata[t]?.region || '') === regionFilter);
    if (typeFilter) list = list.filter(t => (metadata[t]?.type || '') === typeFilter);
    if (styleFilter) list = list.filter(t => (metadata[t]?.style || '') === styleFilter);
    if (tickerSearch) {
      const q = tickerSearch.toLowerCase();
      list = list.filter(t => {
        const label = getTickerLabel(t).toLowerCase();
        return t.toLowerCase().includes(q) || label.includes(q) ||
          (metadata[t]?.region || '').toLowerCase().includes(q) ||
          (metadata[t]?.type || '').toLowerCase().includes(q) ||
          (metadata[t]?.style || '').toLowerCase().includes(q);
      });
    }
    return [...list].sort((a, b) =>
      getTickerLabel(a).localeCompare(getTickerLabel(b), undefined, { sensitivity: 'base' })
    );
  }, [tabTickers, tickerSearch, getTickerLabel, metadata, regionFilter, typeFilter, styleFilter]);

  const getTickerSource = useCallback((ticker) => {
    return dailySet.has(ticker) ? 'daily' : 'monthly';
  }, [dailySet]);

  // ── Series bookkeeping (real tickers + derived pseudo-tickers) ────────────

  const isDerivedKey = (t) => t.startsWith('=');
  const derivedKeys = useMemo(() => derived.map(d => `=${d.id}`), [derived]);
  const plottedTickers = useMemo(
    () => [...selectedTickers, ...derivedKeys],
    [selectedTickers, derivedKeys]
  );

  const getSeriesSource = useCallback(
    (t) => (isDerivedKey(t) ? 'derived' : getTickerSource(t)),
    [getTickerSource]
  );

  const getSeriesLabel = useCallback((t) => {
    if (isDerivedKey(t)) {
      const d = derived.find(x => `=${x.id}` === t);
      return d?.label || t;
    }
    return getTickerLabel(t);
  }, [derived, getTickerLabel]);

  const toggleTicker = (ticker) => {
    setSelectedTickers(prev => {
      if (prev.includes(ticker)) {
        setTickerSettings(s => { const next = { ...s }; delete next[ticker]; return next; });
        return prev.filter(t => t !== ticker);
      }
      const colour = MARKET_DATA_COLORS[(prev.length + derived.length) % MARKET_DATA_COLORS.length];
      setTickerSettings(s => ({ ...s, [ticker]: defaultSeriesSettings(colour) }));
      return [...prev, ticker];
    });
  };

  const updateTickerSetting = (ticker, key, value) => {
    setTickerSettings(prev => ({
      ...prev,
      [ticker]: { ...prev[ticker], [key]: value },
    }));
  };

  const applyToAll = (key, value) => {
    setTickerSettings(prev => {
      const next = { ...prev };
      plottedTickers.forEach(t => {
        if (next[t]) next[t] = { ...next[t], [key]: value };
      });
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedTickers([]);
    setTickerSettings({});
    setDerived([]);
    setChartData({ dates: [], series: {} });
  };

  const addDerivedSeries = () => {
    const { op, a, b, k, window } = derivedDraft;
    if (!a || (op !== 'scale' && !b)) return;
    const id = derived.reduce((m, d) => Math.max(m, d.id), 0) + 1;
    const label = op === 'ratio' ? `${getTickerLabel(a)} / ${getTickerLabel(b)}`
      : op === 'spread' ? `${getTickerLabel(a)} − ${getTickerLabel(b)}`
      : op === 'scale' ? `${getTickerLabel(a)} × ${k}`
      : `Corr(${getTickerLabel(a)}, ${getTickerLabel(b)}, ${window})`;
    const entry = {
      id, op,
      ticker_a: a, source_a: getTickerSource(a),
      ticker_b: op === 'scale' ? null : b,
      source_b: op === 'scale' ? null : getTickerSource(b),
      k: op === 'scale' ? Number(k) : null,
      window: op === 'corr' ? (parseInt(window, 10) || 60) : null,
      label,
    };
    setDerived(prev => [...prev, entry]);
    const colour = MARKET_DATA_COLORS[(selectedTickers.length + derived.length) % MARKET_DATA_COLORS.length];
    setTickerSettings(s => ({ ...s, [`=${id}`]: defaultSeriesSettings(colour) }));
  };

  const removeDerivedSeries = (key) => {
    setDerived(prev => prev.filter(d => `=${d.id}` !== key));
    setTickerSettings(s => { const next = { ...s }; delete next[key]; return next; });
  };

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadChartForTickers = async (tickersToLoad, derivedDefs) => {
    // Base tickers = plotted real tickers plus derived-series inputs.
    const base = new Set(tickersToLoad);
    (derivedDefs || derived).forEach(d => {
      base.add(d.ticker_a);
      if (d.ticker_b) base.add(d.ticker_b);
    });
    if (base.size === 0) return;
    setLoading(true);
    try {
      const bySource = {};
      base.forEach(t => {
        const src = getTickerSource(t);
        if (!bySource[src]) bySource[src] = [];
        bySource[src].push(t);
      });

      const fetches = Object.entries(bySource).map(([src, tickers]) =>
        api.get(`/api/tactical/market-data?source=${encodeURIComponent(src)}&tickers=${encodeURIComponent(tickers.join(','))}&max_points=0`)
          .then(res => ({ source: src, data: res.data }))
      );
      const results = await Promise.all(fetches);

      // Store each series with its own dates (for correct transforms),
      // plus a merged date union for the chart x-axis
      const dateSet = new Set();
      const perTicker = {};
      results.forEach(({ data }) => {
        (data.dates || []).forEach(d => dateSet.add(d));
        Object.entries(data.series || {}).forEach(([ticker, values]) => {
          perTicker[ticker] = { dates: data.dates, values };
        });
      });
      const mergedDates = [...dateSet].sort();
      setChartData({ dates: mergedDates, series: perTicker });
    } catch (err) {
      console.error('Error loading market data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadChart = () => loadChartForTickers(selectedTickers);

  // ── Transform pipeline (mirrors chart_render.build_render_series) ────────

  const derivedValues = useMemo(() => {
    if (!derived.length || !chartData.dates?.length) return {};
    const mergedDates = chartData.dates;
    const remap = (entry) => {
      if (entry.dates === mergedDates) return entry.values.slice();
      const dateMap = {};
      entry.dates.forEach((d, i) => { dateMap[d] = entry.values[i]; });
      return mergedDates.map(d => (dateMap[d] !== undefined ? dateMap[d] : null));
    };
    const out = {};
    derived.forEach(d => {
      const entryA = chartData.series[d.ticker_a];
      if (!entryA) return;
      const a = remap(entryA);
      let b = new Array(mergedDates.length).fill(null);
      if (d.ticker_b) {
        const entryB = chartData.series[d.ticker_b];
        if (!entryB) return;
        b = remap(entryB);
      }
      out[`=${d.id}`] = computeDerived(d, a, b);
    });
    return out;
  }, [derived, chartData]);

  const transformedSeries = useMemo(() => {
    if (!chartData.dates?.length) return {};
    const mergedDates = chartData.dates;
    const result = {};
    plottedTickers.forEach(ticker => {
      let tickerDates, values;
      if (isDerivedKey(ticker)) {
        values = derivedValues[ticker];
        if (!values) return;
        tickerDates = mergedDates;
      } else {
        const entry = chartData.series[ticker];
        if (!entry) return;
        tickerDates = entry.dates;
        values = entry.values;
      }
      const s = tickerSettings[ticker] || defaultSeriesSettings('#072040');

      if (s.resample) {
        [tickerDates, values] = resampleSeries(tickerDates, values, s.resample);
      }
      // Transform on the series' own dense array
      const transformed = applyTransform(values, s.transform, s.window, tickerDates, s.anchor || null);

      // Map onto the shared merged date grid for Chart.js
      if (tickerDates === mergedDates) {
        result[ticker] = transformed;
      } else {
        const dateMap = {};
        tickerDates.forEach((d, i) => { dateMap[d] = transformed[i]; });
        result[ticker] = mergedDates.map(d => (dateMap[d] !== undefined ? dateMap[d] : null));
      }
    });
    return result;
  }, [chartData.series, chartData.dates, plottedTickers, tickerSettings, derivedValues]);

  const hasChartData = chartData.dates && chartData.dates.length > 0;
  const hasRightAxis = chartMode === 'timeseries' &&
    plottedTickers.some(t => tickerSettings[t]?.axis === 'y1');

  // Leading rows where every active series is null are trimmed before the
  // range/zoom applies (chart_render trim_leading_nulls).
  const trimStartIdx = useMemo(() => {
    const activeTickers = plottedTickers.filter(t => transformedSeries[t]);
    if (activeTickers.length === 0 || !chartData.dates) return 0;
    for (let i = 0; i < chartData.dates.length; i++) {
      if (activeTickers.some(t => transformedSeries[t][i] != null)) return i;
    }
    return 0;
  }, [plottedTickers, transformedSeries, chartData.dates]);

  const trimmedDates = useMemo(
    () => (chartData.dates || []).slice(trimStartIdx),
    [chartData.dates, trimStartIdx]
  );

  const dateRangeV2 = useMemo(() => ({
    mode: rangeMode === 'all' || rangeMode === 'fixed' ? rangeMode : 'trailing',
    trailing: rangeMode !== 'all' && rangeMode !== 'fixed' ? rangeMode : null,
    start: rangeMode === 'fixed' ? (fixedStart || null) : null,
    end: rangeMode === 'fixed' ? (fixedEnd || null) : null,
    zoom: { start_pct: xStart, end_pct: xEnd },
  }), [rangeMode, fixedStart, fixedEnd, xStart, xEnd]);

  const [rangeLo, rangeHi] = useMemo(
    () => resolveDateRange(dateRangeV2, trimmedDates),
    [dateRangeV2, trimmedDates]
  );

  const windowDates = useMemo(
    () => trimmedDates.slice(rangeLo, rangeHi),
    [trimmedDates, rangeLo, rangeHi]
  );

  const windowedSeries = useCallback((ticker) => {
    const arr = transformedSeries[ticker];
    if (!arr) return null;
    return arr.slice(trimStartIdx).slice(rangeLo, rangeHi);
  }, [transformedSeries, trimStartIdx, rangeLo, rangeHi]);

  const activePlotted = useMemo(
    () => plottedTickers.filter(t => transformedSeries[t]),
    [plottedTickers, transformedSeries]
  );

  // Mode selects default to the first active series.
  useEffect(() => {
    if (activePlotted.length === 0) return;
    if (!activePlotted.includes(histTicker)) setHistTicker(activePlotted[0]);
    if (!activePlotted.includes(scatterX)) setScatterX(activePlotted[0]);
    if (!activePlotted.includes(seasonalTicker)) setSeasonalTicker(activePlotted[0]);
  }, [activePlotted, histTicker, scatterX, seasonalTicker]);

  const seriesLegendLabel = useCallback((t) => {
    const s = tickerSettings[t] || {};
    const suffix = getTransformLabel(s.transform, s.window, s.anchor);
    return getSeriesLabel(t) + (suffix ? ' ' + suffix : '');
  }, [tickerSettings, getSeriesLabel]);

  // ── Chart.js data per mode ────────────────────────────────────────────────

  const chartJsData = useMemo(() => {
    if (!hasChartData || activePlotted.length === 0) return null;

    try {
      if (chartMode === 'histogram') {
        const t = activePlotted.includes(histTicker) ? histTicker : activePlotted[0];
        const values = (windowedSeries(t) || []).filter(v => v != null && Number.isFinite(v));
        const { edges, counts } = computeHistogram(values, histBins);
        const s = tickerSettings[t] || {};
        const fmt = makeTickFormatter(toAxisV2(axisConfig.y)) ||
          ((v) => v.toLocaleString('en-AU', { maximumFractionDigits: Math.abs(edges[1] - edges[0]) >= 1 ? 1 : 3 }));
        const labels = counts.map((_, i) => `${fmt(edges[i])} – ${fmt(edges[i + 1])}`);
        return {
          labels,
          datasets: [{
            type: 'bar',
            label: seriesLegendLabel(t),
            ticker: t,
            data: counts,
            backgroundColor: s.colour || '#072040',
            borderColor: s.colour || '#072040',
          }],
        };
      }

      if (chartMode === 'scatter') {
        const xT = activePlotted.includes(scatterX) ? scatterX : activePlotted[0];
        const xVals = windowedSeries(xT) || [];
        const n = windowDates.length;
        const datasets = [];
        activePlotted.filter(t => t !== xT).forEach(t => {
          const yVals = windowedSeries(t) || [];
          const s = tickerSettings[t] || {};
          const colour = s.colour || '#072040';
          const pts = [];
          const idxs = [];
          for (let i = 0; i < n; i++) {
            const x = xVals[i], y = yVals[i];
            if (x != null && y != null && Number.isFinite(x) && Number.isFinite(y)) {
              pts.push({ x, y, d: windowDates[i] });
              idxs.push(i);
            }
          }
          datasets.push({
            type: 'scatter',
            label: seriesLegendLabel(t),
            ticker: t,
            data: pts,
            pointRadius: 2.5,
            backgroundColor: recencyFade && n > 1
              ? idxs.map(i => `${colour}${Math.round((0.25 + 0.75 * (i / (n - 1))) * 255).toString(16).padStart(2, '0')}`)
              : colour,
            borderColor: colour,
          });
          if (trendLine && pts.length >= 2) {
            const [slope, intercept] = olsFit(pts.map(p => p.x), pts.map(p => p.y));
            const x0 = Math.min(...pts.map(p => p.x));
            const x1 = Math.max(...pts.map(p => p.x));
            datasets.push({
              type: 'line',
              label: `${getSeriesLabel(t)} trend`,
              skipLegend: true,
              skipLastValueLabel: true,
              data: [{ x: x0, y: slope * x0 + intercept }, { x: x1, y: slope * x1 + intercept }],
              borderColor: colour,
              borderWidth: 1.2,
              borderDash: [6, 4],
              pointRadius: 0,
              fill: false,
            });
          }
        });
        return { labels: [], datasets, scatterXLabel: seriesLegendLabel(xT) };
      }

      if (chartMode === 'seasonal') {
        const t = activePlotted.includes(seasonalTicker) ? seasonalTicker : activePlotted[0];
        const values = windowedSeries(t) || [];
        const traces = seasonalSplit(windowDates, values, seasonalYears);
        const s = tickerSettings[t] || {};
        const colour = s.colour || '#072040';
        const nPrior = Math.max(1, traces.length - 1);
        return {
          labels: [],
          datasets: traces.map((trace, rank) => {
            const isCurrent = rank === traces.length - 1;
            const alpha = 0.25 + 0.45 * (rank / nPrior);
            return {
              type: 'line',
              label: String(trace.year),
              data: trace.points.map(([x, y]) => ({ x, y })),
              borderColor: isCurrent ? colour
                : `rgba(108, 117, 125, ${alpha.toFixed(2)})`,
              backgroundColor: 'transparent',
              borderWidth: isCurrent ? 2 : 1,
              pointRadius: 0,
              fill: false,
              spanGaps: true,
            };
          }),
        };
      }

      // timeseries
      const datasets = activePlotted.map(ticker => {
        const s = tickerSettings[ticker] || defaultSeriesSettings('#072040');
        const colour = s.colour || '#072040';
        const base = {
          label: seriesLegendLabel(ticker),
          ticker,
          data: windowedSeries(ticker),
          yAxisID: s.axis || 'y',
          spanGaps: true,
        };
        if (s.type === 'bar') {
          return {
            ...base,
            type: 'bar',
            backgroundColor: colour,
            borderColor: colour,
            ...(stacked ? { stack: s.axis || 'y' } : {}),
          };
        }
        if (s.type === 'area') {
          return {
            ...base,
            type: 'line',
            fill: stacked ? 'stack' : 'origin',
            backgroundColor: `${colour}40`,
            borderColor: colour,
            borderWidth: s.lineWidth,
            borderDash: BORDER_DASH[s.lineStyle] || [],
            pointRadius: 0,
            tension: 0,
          };
        }
        return {
          ...base,
          type: 'line',
          fill: false,
          backgroundColor: colour,
          borderColor: colour,
          borderWidth: s.lineWidth,
          borderDash: BORDER_DASH[s.lineStyle] || [],
          pointRadius: 0,
          tension: 0,
        };
      });
      return { labels: windowDates, datasets };
    } catch (err) {
      return { error: err.message };
    }
  }, [hasChartData, activePlotted, chartMode, histTicker, histBins, scatterX,
      trendLine, recencyFade, seasonalTicker, seasonalYears, stacked,
      tickerSettings, windowDates, windowedSeries, seriesLegendLabel,
      getSeriesLabel, axisConfig.y]);

  // Mean ± SD bands for flagged series, over the visible window (same slice
  // as the server renderer).
  const sdBands = useMemo(() => {
    if (chartMode !== 'timeseries') return [];
    const bands = [];
    activePlotted.forEach(t => {
      const s = tickerSettings[t];
      if (!s?.band) return;
      const vals = (windowedSeries(t) || []).filter(v => v != null && Number.isFinite(v));
      if (vals.length < 2) return;
      const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
      const sd = Math.sqrt(vals.reduce((a, v) => a + (v - mean) ** 2, 0) / vals.length);
      bands.push({ axis: s.axis || 'y', mean, sd, nSd: s.band === 'sd1' ? 1 : 2, colour: s.colour || '#072040' });
    });
    return bands;
  }, [chartMode, activePlotted, tickerSettings, windowedSeries]);

  const axisV2 = useMemo(() => ({
    y: toAxisV2(axisConfig.y),
    y1: toAxisV2(axisConfig.y1),
  }), [axisConfig]);

  const chartOptions = useMemo(() => {
    const legend = { show: legendShow, position: legendPosition };
    if (chartMode === 'timeseries') {
      const opts = makeMarketDataChartOptions(axisV2, hasRightAxis, tickerSettings,
        { gridlines, legend, stacked });
      opts.plugins.zeroLine = { enabled: zeroLine };
      opts.plugins.lastValueLabels = {
        enabled: lastValues,
        format: {
          y: makeTickFormatter(axisV2.y) || undefined,
          y1: makeTickFormatter(axisV2.y1) || undefined,
        },
      };
      opts.plugins.chartAnnotations = {
        bands: annBands,
        preset_bands: recessionBands ? 'us_recessions' : null,
        vlines: annVlines,
        hlines: annHlines,
      };
      opts.plugins.sdBands = { bands: sdBands };
      return opts;
    }

    const yFmt = makeTickFormatter(axisV2.y);
    const shared = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        tooltip: {
          backgroundColor: '#072040',
          borderColor: '#f0a500',
          borderWidth: 1,
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
        },
        legend: {
          display: legendShow,
          position: legendPosition === 'bottom' ? 'bottom' : legendPosition === 'right' ? 'right' : 'top',
          labels: {
            usePointStyle: true, boxWidth: 8, font: { size: 11 },
            filter: (item, data) => !data.datasets[item.datasetIndex]?.skipLegend,
          },
        },
      },
    };

    if (chartMode === 'histogram') {
      return {
        ...shared,
        scales: {
          x: { ticks: { font: { size: 10 }, maxTicksLimit: 12 },
               grid: { display: false } },
          y: { beginAtZero: true,
               title: { display: true, text: 'Count', color: '#072040', font: { size: 11 } },
               ticks: { font: { size: 10 } },
               grid: { display: gridlines, color: '#E7E6E6' } },
        },
      };
    }

    if (chartMode === 'scatter') {
      return {
        ...shared,
        plugins: {
          ...shared.plugins,
          tooltip: {
            ...shared.plugins.tooltip,
            callbacks: {
              title: (items) => {
                const d = items[0]?.raw?.d;
                return d ? new Date(d).toLocaleDateString('en-AU') : '';
              },
              label: (ctx) => `${ctx.dataset.label}: (${ctx.parsed.x?.toFixed(3)}, ${ctx.parsed.y?.toFixed(3)})`,
            },
          },
        },
        scales: {
          x: {
            type: 'linear',
            title: { display: true, text: chartJsData?.scatterXLabel || '', color: '#072040', font: { size: 11 } },
            ticks: { font: { size: 10 } },
            grid: { display: gridlines, color: '#E7E6E6' },
          },
          y: {
            type: axisV2.y.log ? 'logarithmic' : 'linear',
            reverse: axisV2.y.invert,
            min: axisV2.y.min ?? undefined,
            max: axisV2.y.max ?? undefined,
            ticks: { font: { size: 10 }, ...(yFmt ? { callback: (v) => yFmt(v) } : {}) },
            grid: { display: gridlines, color: '#E7E6E6' },
          },
        },
      };
    }

    // seasonal
    return {
      ...shared,
      plugins: {
        ...shared.plugins,
        tooltip: { ...shared.plugins.tooltip, callbacks: {
          title: () => '',
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2)}`,
        } },
      },
      scales: {
        x: {
          type: 'linear',
          min: 0, max: 366,
          ticks: {
            font: { size: 10 },
            callback: (v) => {
              const i = MONTH_CUM_DAYS.indexOf(v);
              return i >= 0 ? MONTH_NAMES[i] : '';
            },
            stepSize: 1,
            autoSkip: false,
            includeBounds: false,
          },
          afterBuildTicks: (axis) => {
            axis.ticks = MONTH_CUM_DAYS.map(v => ({ value: v }));
          },
          grid: { display: false },
        },
        y: {
          type: axisV2.y.log ? 'logarithmic' : 'linear',
          reverse: axisV2.y.invert,
          min: axisV2.y.min ?? undefined,
          max: axisV2.y.max ?? undefined,
          ticks: { font: { size: 10 }, ...(yFmt ? { callback: (v) => yFmt(v) } : {}) },
          grid: { display: gridlines, color: '#E7E6E6' },
        },
      },
    };
  }, [chartMode, axisV2, hasRightAxis, tickerSettings, gridlines, legendShow,
      legendPosition, stacked, zeroLine, lastValues, annBands, recessionBands,
      annVlines, annHlines, sdBands, chartJsData?.scatterXLabel]);

  // ── Export: PNG download, clipboard copy, CSV ─────────────────────────────

  const buildExportCanvas = () => {
    const chart = chartRef.current;
    if (!chart) return null;
    // PowerPoint 16:9 slide at 2x DPI = 1920x1080
    const exportW = 1920;
    const exportH = 1080;
    const footerH = footnote.trim() ? 64 : 40;

    // Uniform export scale: every font, line width and point radius is
    // multiplied by the same factor, so the export is the on-screen chart
    // magnified — nothing balloons relative to anything else.
    const K = 2;
    const touched = [];
    const scaleProp = (obj, key, round = false) => {
      if (!obj || typeof obj[key] !== 'number' || obj[key] === 0) return;
      touched.push([obj, key, obj[key]]);
      obj[key] = round ? Math.round(obj[key] * K) : obj[key] * K;
    };

    const opts = chart.options;
    const legendLabels = opts.plugins.legend?.labels;
    scaleProp(legendLabels?.font, 'size', true);
    scaleProp(legendLabels, 'boxWidth');
    ['x', 'y', 'y1'].forEach(k => {
      const sc = opts.scales?.[k];
      if (!sc) return;
      scaleProp(sc.ticks?.font, 'size', true);
      scaleProp(sc.title?.font, 'size', true);
    });
    // Custom canvas plugins (annotation labels, last-value labels) read this.
    ['chartAnnotations', 'lastValueLabels', 'sdBands', 'zeroLine'].forEach(name => {
      const cfg = opts.plugins?.[name];
      if (!cfg) return;
      touched.push([cfg, 'fontScale', cfg.fontScale]);
      cfg.fontScale = K;
    });
    chart.data.datasets.forEach(ds => {
      scaleProp(ds, 'borderWidth');
      scaleProp(ds, 'pointRadius');
    });

    // Resize the chart to exact export dimensions so Chart.js re-renders natively
    const container = chart.canvas.parentNode;
    const origW = container.style.width;
    const origH = container.style.height;
    container.style.width = exportW + 'px';
    container.style.height = (exportH - footerH) + 'px';
    chart.resize(exportW, exportH - footerH);

    // Compose final image: chart + footnote/source lines
    const canvas = document.createElement('canvas');
    canvas.width = exportW;
    canvas.height = exportH;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exportW, exportH);
    ctx.drawImage(chart.canvas, 0, 0, exportW, exportH - footerH);
    ctx.textAlign = 'left';
    if (footnote.trim()) {
      ctx.fillStyle = '#072040';
      ctx.font = '18px sans-serif';
      ctx.fillText(footnote.trim(), 20, exportH - 36);
    }
    ctx.fillStyle = '#6c757d';
    ctx.font = '18px sans-serif';
    ctx.fillText(sourceText, 20, exportH - 12);

    // Restore everything we scaled, then the on-screen size
    touched.reverse().forEach(([obj, key, value]) => { obj[key] = value; });
    container.style.width = origW;
    container.style.height = origH;
    chart.resize();

    return canvas;
  };

  const handleExportChart = () => {
    const canvas = buildExportCanvas();
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'market-data-chart.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleCopyChart = async () => {
    const canvas = buildExportCanvas();
    if (!canvas) return;
    try {
      const blob = await new Promise((resolve, reject) =>
        canvas.toBlob(b => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))), 'image/png'));
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setConfigNotice('Chart copied to clipboard');
    } catch (err) {
      setConfigError('Copy to clipboard failed: ' + err.message);
    }
  };

  const handleExportCsv = () => {
    if (!windowDates.length || activePlotted.length === 0) return;
    const cols = activePlotted.filter(t => transformedSeries[t]);
    const header = ['Date', ...cols.map(t => `"${seriesLegendLabel(t).replaceAll('"', '""')}"`)];
    const lines = [header.join(',')];
    const seriesArrays = cols.map(t => windowedSeries(t));
    windowDates.forEach((d, i) => {
      lines.push([d, ...seriesArrays.map(arr => (arr[i] != null && Number.isFinite(arr[i]) ? arr[i] : ''))].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.download = 'market-data.csv';
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // ── Saved chart configs ──────────────────────────────────────────────────

  // Schema v2 snapshot of the current chart.
  const collectConfigSnapshot = () => ({
    schema_version: 2,
    title: chartTitle.trim() || null,
    subtitle: chartSubtitle.trim() || null,
    chart_mode: chartMode,
    series: plottedTickers.map(t => {
      const s = tickerSettings[t] || defaultSeriesSettings('#072040');
      return {
        ticker: t,
        source: getSeriesSource(t),
        axis: s.axis || 'y',
        transform: s.transform || 'none',
        window: s.window || 20,
        anchor: s.anchor || null,
        colour: s.colour || '#072040',
        label: getSeriesLabel(t),
        type: s.type || 'line',
        line_width: Number(s.lineWidth) || 1.5,
        line_style: s.lineStyle || 'solid',
        band: s.band || null,
        resample: s.resample || null,
      };
    }),
    axis_config: axisV2,
    date_range: dateRangeV2,
    options: {
      stacked,
      gridlines,
      legend: { show: legendShow, position: legendPosition },
      source_text: sourceText.trim() || DEFAULT_SOURCE_TEXT,
      zero_line: zeroLine,
      last_value_labels: lastValues,
    },
    histogram: chartMode === 'histogram' ? { ticker: histTicker, bins: histBins } : null,
    scatter: chartMode === 'scatter'
      ? { x_ticker: scatterX, trend_line: trendLine, recency_fade: recencyFade } : null,
    seasonal: chartMode === 'seasonal'
      ? { ticker: seasonalTicker, years: seasonalYears, highlight_current: true } : null,
    derived,
    annotations: {
      bands: annBands,
      preset_bands: recessionBands ? 'us_recessions' : null,
      vlines: annVlines,
      hlines: annHlines,
    },
    footnote: footnote.trim() || null,
  });

  const handleSaveSubmit = async () => {
    if (!saveName.trim()) return;
    setSavingConfig(true);
    setConfigError('');
    try {
      const created = await marketDataAPI.createChartConfig({
        name: saveName.trim(),
        description: saveDescription.trim() || null,
        is_favourite: saveFavourite,
        config: collectConfigSnapshot(),
      });
      setLoadedConfig(created);
      setShowSaveModal(false);
      setConfigNotice(`Saved "${created.name}"`);
      refreshSavedConfigs();
    } catch (err) {
      setConfigError('Failed to save chart: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleOverwriteLoaded = async () => {
    if (!loadedConfig) return;
    setSavingConfig(true);
    setConfigError('');
    try {
      const updated = await marketDataAPI.updateChartConfig(loadedConfig.id, {
        config: collectConfigSnapshot(),
      });
      setLoadedConfig(updated);
      setConfigNotice(`Updated "${updated.name}"`);
      refreshSavedConfigs();
    } catch (err) {
      setConfigError('Failed to update chart: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleLoadConfig = async (id) => {
    setConfigError('');
    setConfigNotice('');
    try {
      const row = await marketDataAPI.getChartConfig(id);
      let cfg;
      try {
        cfg = upgradeChartConfig(row.config || {});
      } catch (err) {
        setConfigError(`"${row.name}" not loaded — ${err.message}`);
        return;
      }
      const series = Array.isArray(cfg.series) ? cfg.series : [];
      const realSeries = series.filter(s => s.source !== 'derived');
      const derivedRefs = series.filter(s => s.source === 'derived');
      const cfgDerived = cfg.derived || [];
      const baseNeeded = [
        ...realSeries.map(s => s.ticker),
        ...cfgDerived.flatMap(d => [d.ticker_a, d.ticker_b]).filter(Boolean),
      ];
      const missing = [...new Set(baseNeeded)]
        .filter(t => !dailySet.has(t) && !monthlySet.has(t));
      const present = realSeries.filter(s => dailySet.has(s.ticker) || monthlySet.has(s.ticker));
      if (missing.length > 0) {
        const names = missing.join(', ');
        if (present.length === 0) {
          setConfigError(`"${row.name}" not loaded — tickers no longer available: ${names}`);
          return;
        }
        setConfigError(`"${row.name}" loaded without unavailable tickers: ${names}`);
      }
      const usableDerived = cfgDerived.filter(d =>
        !missing.includes(d.ticker_a) && (!d.ticker_b || !missing.includes(d.ticker_b)));
      const usableDerivedKeys = new Set(usableDerived.map(d => `=${d.id}`));

      const tickers = present.map(s => s.ticker);
      const settings = {};
      series.forEach(s => {
        const key = s.ticker;
        if (s.source === 'derived' ? !usableDerivedKeys.has(key)
          : !present.some(p => p.ticker === key)) return;
        settings[key] = {
          axis: s.axis || 'y',
          transform: s.transform || 'none',
          window: s.window || 20,
          anchor: s.anchor || '',
          colour: s.colour || '#072040',
          type: s.type || 'line',
          lineWidth: s.line_width || 1.5,
          lineStyle: s.line_style || 'solid',
          band: s.band || null,
          resample: s.resample || null,
        };
      });
      setSelectedTickers(tickers);
      setDerived(usableDerived.filter(d => derivedRefs.some(r => r.ticker === `=${d.id}`)));
      setTickerSettings(settings);
      setAxisConfig({
        y: fromAxisV2(cfg.axis_config?.y),
        y1: fromAxisV2(cfg.axis_config?.y1),
      });

      const dr = cfg.date_range || {};
      if (dr.mode === 'trailing') setRangeMode(dr.trailing || '5y');
      else setRangeMode(dr.mode || 'all');
      setFixedStart(dr.start || '');
      setFixedEnd(dr.end || '');
      setXStart(dr.zoom?.start_pct ?? 0);
      setXEnd(dr.zoom?.end_pct ?? 100);

      setChartMode(cfg.chart_mode || 'timeseries');
      setStacked(!!cfg.options?.stacked);
      setGridlines(!!cfg.options?.gridlines);
      setZeroLine(!!cfg.options?.zero_line);
      setLastValues(!!cfg.options?.last_value_labels);
      setLegendShow(cfg.options?.legend?.show !== false);
      setLegendPosition(cfg.options?.legend?.position || 'bottom');
      setSourceText(cfg.options?.source_text || DEFAULT_SOURCE_TEXT);
      setFootnote(cfg.footnote || '');
      if (cfg.histogram) { setHistTicker(cfg.histogram.ticker); setHistBins(cfg.histogram.bins); }
      if (cfg.scatter) {
        setScatterX(cfg.scatter.x_ticker);
        setTrendLine(!!cfg.scatter.trend_line);
        setRecencyFade(!!cfg.scatter.recency_fade);
      }
      if (cfg.seasonal) { setSeasonalTicker(cfg.seasonal.ticker); setSeasonalYears(cfg.seasonal.years); }
      const ann = cfg.annotations || {};
      setRecessionBands(ann.preset_bands === 'us_recessions');
      setAnnBands(ann.bands || []);
      setAnnVlines(ann.vlines || []);
      setAnnHlines(ann.hlines || []);

      setChartTitle(cfg.title || '');
      setChartSubtitle(cfg.subtitle || '');
      setLoadedConfig(row);
      setShowSavedPanel(false);
      await loadChartForTickers(tickers, usableDerived);
    } catch (err) {
      setConfigError('Failed to load chart: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteConfig = async (row) => {
    if (!window.confirm(`Delete saved chart "${row.name}"?`)) return;
    setConfigError('');
    try {
      await marketDataAPI.deleteChartConfig(row.id);
      if (loadedConfig?.id === row.id) setLoadedConfig(null);
      refreshSavedConfigs();
    } catch (err) {
      setConfigError('Failed to delete chart: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleFavourite = async (row) => {
    try {
      await marketDataAPI.updateChartConfig(row.id, { is_favourite: !row.is_favourite });
      refreshSavedConfigs();
    } catch (err) {
      setConfigError('Failed to update favourite: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openSaveModal = () => {
    setSaveName(loadedConfig ? loadedConfig.name : '');
    setSaveDescription(loadedConfig?.description || '');
    setSaveFavourite(false);
    setShowSaveModal(true);
  };

  // ── Small render helpers ──────────────────────────────────────────────────

  const axisPill = (axisKey, cfgKey, label) => (
    <button
      className={`market-data-axis-btn${axisConfig[axisKey][cfgKey] ? ' active' : ''}`}
      onClick={() => setAxisConfig(prev => ({
        ...prev, [axisKey]: { ...prev[axisKey], [cfgKey]: !prev[axisKey][cfgKey] },
      }))}
    >{label}</button>
  );

  const axisNumInput = (axisKey, cfgKey, placeholder, width = 64) => (
    <input
      className="market-data-window-input"
      style={{ width }}
      type="number"
      placeholder={placeholder}
      value={axisConfig[axisKey][cfgKey]}
      onChange={(e) => setAxisConfig(prev => ({
        ...prev, [axisKey]: { ...prev[axisKey], [cfgKey]: e.target.value },
      }))}
    />
  );

  const axisToolbarGroup = (axisKey, label) => (
    <div className="market-data-toolbar-group">
      <span>{label}:</span>
      {axisPill(axisKey, 'invert', 'Invert')}
      {axisPill(axisKey, 'log', 'Log')}
      {axisNumInput(axisKey, 'min', 'Min')}
      {axisNumInput(axisKey, 'max', 'Max')}
      {axisNumInput(axisKey, 'decimals', 'Dp', 46)}
      {axisPill(axisKey, 'pctSuffix', '%')}
      {axisPill(axisKey, 'thousands', "1,000s")}
    </div>
  );

  const seriesSelect = (value, onChange) => (
    <select className="market-data-transform-select" value={value} onChange={onChange}>
      {activePlotted.map(t => <option key={t} value={t}>{getSeriesLabel(t)}</option>)}
    </select>
  );

  const anyBars = plottedTickers.some(t => ['bar', 'area'].includes(tickerSettings[t]?.type));
  const mixedBarFrequencies = chartMode === 'timeseries' &&
    plottedTickers.some(t => tickerSettings[t]?.type === 'bar' && getSeriesSource(t) === 'daily' && !tickerSettings[t]?.resample) &&
    plottedTickers.some(t => getSeriesSource(t) === 'monthly');

  return (
    <div className="market-data-page">
      <h1>Market and Economic Data</h1>
      <ManagedDisclaimer dkey="data_notice_refinitiv" as="p" className="market-data-disclaimer" />
      {configError && (
        <div className="alert alert-warning py-2 px-3" style={{ fontSize: '0.85rem' }}>
          {configError}
          <button type="button" className="btn-close float-end" style={{ fontSize: '0.6rem' }} onClick={() => setConfigError('')} />
        </div>
      )}
      {configNotice && (
        <div className="alert alert-success py-2 px-3" style={{ fontSize: '0.85rem' }}>
          {configNotice}
          <button type="button" className="btn-close float-end" style={{ fontSize: '0.6rem' }} onClick={() => setConfigNotice('')} />
        </div>
      )}

      <div className="market-data-section">
        {/* Saved chart configs (hidden for external users) */}
        {savedChartsEnabled && (
        <div style={{ marginBottom: 12 }}>
          <button
            className={`market-data-axis-btn${showSavedPanel ? ' active' : ''}`}
            onClick={() => { setShowSavedPanel(p => !p); if (!showSavedPanel) refreshSavedConfigs(); }}
          >
            Saved Charts ({savedConfigs.length})
          </button>
          {loadedConfig && (
            <span style={{ marginLeft: 10, fontSize: '0.8rem', color: '#6c757d' }}>
              Loaded: <strong>{loadedConfig.name}</strong>
            </span>
          )}
          {showSavedPanel && (
            <div style={{ marginTop: 8, border: '1px solid #E7E6E6', borderRadius: 4, maxHeight: 260, overflowY: 'auto' }}>
              {savedConfigs.length === 0 && (
                <div style={{ padding: '10px 12px', color: '#6c757d', fontSize: '0.83rem' }}>
                  No saved charts yet — load a chart and press Save Chart.
                </div>
              )}
              {savedConfigs.map(row => (
                <div
                  key={row.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderBottom: '1px solid #E7E6E6', fontSize: '0.83rem' }}
                >
                  <button
                    className="market-data-axis-btn"
                    style={{ minWidth: 20 }}
                    title={row.is_favourite ? 'Remove favourite' : 'Mark favourite'}
                    onClick={() => handleToggleFavourite(row)}
                  >{row.is_favourite ? '★' : '☆'}</button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong>{row.name}</strong>
                    {row.description && <span style={{ color: '#6c757d' }}> — {row.description}</span>}
                    <div style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                      {row.created_by_email || 'Unknown'} · updated {row.updated_at ? new Date(row.updated_at).toLocaleDateString('en-AU') : '-'}
                    </div>
                  </div>
                  <button className="market-data-axis-btn" onClick={() => handleLoadConfig(row.id)}>Load</button>
                  {row.can_edit && (
                    <button className="market-data-remove-btn" title="Delete" onClick={() => handleDeleteConfig(row)}>×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Classification tabs */}
        <div className="market-data-classification-tabs">
          {classificationTabs.map(tab => (
            <button
              key={tab.name}
              className={`market-data-classification-tab${selectedClassification === tab.name ? ' active' : ''}`}
              onClick={() => { setSelectedClassification(tab.name); setTickerSearch(''); setRegionFilter(''); setTypeFilter(''); setStyleFilter(''); }}
            >
              {tab.name}
              <span className="market-data-tab-count">{tab.tickers.length}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <select
            className="market-data-filter-select"
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
          >
            <option value="">All Regions</option>
            {marketFilterOptions.regions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select
            className="market-data-filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            {marketFilterOptions.types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            className="market-data-filter-select"
            value={styleFilter}
            onChange={(e) => setStyleFilter(e.target.value)}
          >
            <option value="">All Styles</option>
            {marketFilterOptions.styles.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="market-data-ticker-selector">
          <input
            className="market-data-ticker-search"
            type="text"
            placeholder="Search tickers in this tab..."
            value={tickerSearch}
            onChange={(e) => setTickerSearch(e.target.value)}
          />
          <div className="market-data-ticker-list">
            {filteredTickers.map(ticker => (
              <label key={ticker} className="market-data-ticker-item">
                <input
                  type="checkbox"
                  checked={selectedTickers.includes(ticker)}
                  onChange={() => toggleTicker(ticker)}
                />
                {getTickerLabel(ticker)}
                {getTickerLabel(ticker) !== ticker && (
                  <span className="market-data-ticker-code">({ticker})</span>
                )}
              </label>
            ))}
            {filteredTickers.length === 0 && (
              <div style={{ padding: '8px 12px', color: '#6c757d', fontSize: '0.83rem' }}>
                No tickers found
              </div>
            )}
          </div>
        </div>

        <div className="market-data-actions">
          <button
            className="backtest-btn backtest-btn-primary"
            onClick={handleLoadChart}
            disabled={selectedTickers.length === 0 || loading}
            style={{ width: 'auto', padding: '8px 24px' }}
          >
            {loading ? 'Loading...' : 'Load Chart'}
          </button>
          {selectedTickers.length > 0 && (
            <button
              className="backtest-btn-clear"
              onClick={handleClearSelection}
              style={{ padding: '8px 16px' }}
            >
              Clear Selection
            </button>
          )}
          <span className="market-data-selected-count">
            {selectedTickers.length} ticker{selectedTickers.length !== 1 ? 's' : ''} selected
          </span>
        </div>

        {/* Series settings panel */}
        {plottedTickers.length > 0 && (
          <div className="market-data-ticker-settings">
            {plottedTickers.map((ticker) => {
              const s = tickerSettings[ticker] || defaultSeriesSettings('#072040');
              const tf = TRANSFORMS.find(t => t.value === s.transform);
              const transformFreq = isDerivedKey(ticker) ? 'daily' : getTickerSource(ticker);
              return (
                <div key={ticker} className="market-data-ticker-settings-row">
                  <input
                    type="color"
                    className="market-data-colour-input"
                    value={s.colour || '#072040'}
                    onChange={(e) => updateTickerSetting(ticker, 'colour', e.target.value)}
                    title="Series colour"
                  />
                  <span className="market-data-ticker-settings-label">
                    {getSeriesLabel(ticker)}
                    {isDerivedKey(ticker) && <span className="market-data-ticker-code">(derived)</span>}
                  </span>
                  <button
                    className={`market-data-axis-btn${s.axis === 'y' ? ' active' : ''}`}
                    onClick={() => updateTickerSetting(ticker, 'axis', 'y')}
                  >L</button>
                  <button
                    className={`market-data-axis-btn${s.axis === 'y1' ? ' active' : ''}`}
                    onClick={() => updateTickerSetting(ticker, 'axis', 'y1')}
                  >R</button>
                  <select
                    className="market-data-transform-select"
                    value={s.type || 'line'}
                    onChange={(e) => updateTickerSetting(ticker, 'type', e.target.value)}
                    title="Series type"
                  >
                    <option value="line">Line</option>
                    <option value="bar">Bar</option>
                    <option value="area">Area</option>
                  </select>
                  {s.type !== 'bar' && (
                    <>
                      <input
                        className="market-data-window-input"
                        style={{ width: 56 }}
                        type="number"
                        min="0.5" max="6" step="0.5"
                        value={s.lineWidth}
                        onChange={(e) => updateTickerSetting(ticker, 'lineWidth', Number(e.target.value) || 1.5)}
                        title="Line width"
                      />
                      <select
                        className="market-data-transform-select"
                        value={s.lineStyle || 'solid'}
                        onChange={(e) => updateTickerSetting(ticker, 'lineStyle', e.target.value)}
                        title="Line style"
                      >
                        <option value="solid">Solid</option>
                        <option value="dashed">Dashed</option>
                        <option value="dotted">Dotted</option>
                      </select>
                    </>
                  )}
                  <select
                    className="market-data-transform-select"
                    value={s.transform}
                    onChange={(e) => {
                      const newTf = TRANSFORMS.find(t => t.value === e.target.value);
                      updateTickerSetting(ticker, 'transform', e.target.value);
                      if (newTf?.needsWindow && !s.window) {
                        updateTickerSetting(ticker, 'window', newTf.defaultWindow);
                      }
                    }}
                  >
                    {getTransformsForFreq(transformFreq).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  {tf?.needsWindow && (
                    <input
                      className="market-data-window-input"
                      type="number"
                      min="2"
                      value={s.window || tf.defaultWindow}
                      onChange={(e) => updateTickerSetting(ticker, 'window', parseInt(e.target.value) || 2)}
                    />
                  )}
                  {tf?.needsAnchor && (
                    <input
                      className="market-data-date-input"
                      type="date"
                      value={s.anchor || ''}
                      onChange={(e) => updateTickerSetting(ticker, 'anchor', e.target.value)}
                      title="Rebase anchor date"
                    />
                  )}
                  <select
                    className="market-data-transform-select"
                    value={s.band || ''}
                    onChange={(e) => updateTickerSetting(ticker, 'band', e.target.value || null)}
                    title="Mean ± standard deviation bands over the visible window"
                  >
                    <option value="">No bands</option>
                    <option value="sd1">±1 SD</option>
                    <option value="sd2">±2 SD</option>
                  </select>
                  {!isDerivedKey(ticker) && getTickerSource(ticker) === 'daily' && (
                    <select
                      className="market-data-transform-select"
                      value={s.resample || ''}
                      onChange={(e) => updateTickerSetting(ticker, 'resample', e.target.value || null)}
                      title="Resample frequency"
                    >
                      <option value="">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  )}
                  <button
                    className="market-data-remove-btn"
                    onClick={() => (isDerivedKey(ticker) ? removeDerivedSeries(ticker) : toggleTicker(ticker))}
                    title="Remove"
                  >×</button>
                </div>
              );
            })}
            <div className="market-data-apply-all-row">
              <span style={{ fontSize: '0.8rem', fontWeight: 500, marginRight: 8 }}>Apply to All:</span>
              <select
                className="market-data-transform-select"
                defaultValue=""
                onChange={(e) => { if (e.target.value) applyToAll('transform', e.target.value); e.target.value = ''; }}
              >
                <option value="" disabled>Transform...</option>
                {TRANSFORMS.filter(t => !t.freq).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <button
                className={`market-data-axis-btn`}
                onClick={() => applyToAll('axis', 'y')}
              >All L</button>
              <button
                className={`market-data-axis-btn`}
                onClick={() => applyToAll('axis', 'y1')}
              >All R</button>
              <select
                className="market-data-transform-select"
                defaultValue=""
                onChange={(e) => { if (e.target.value) applyToAll('type', e.target.value); e.target.value = ''; }}
              >
                <option value="" disabled>Type...</option>
                <option value="line">Line</option>
                <option value="bar">Bar</option>
                <option value="area">Area</option>
              </select>
            </div>
            {/* Derived expression series (Datastream-style ratio/spread/correlation) */}
            {selectedTickers.length >= 1 && (
              <div className="market-data-apply-all-row">
                <span style={{ fontSize: '0.8rem', fontWeight: 500, marginRight: 8 }}>Add derived series:</span>
                <select
                  className="market-data-transform-select"
                  value={derivedDraft.op}
                  onChange={(e) => setDerivedDraft(d => ({ ...d, op: e.target.value }))}
                >
                  {DERIVED_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select
                  className="market-data-transform-select"
                  value={derivedDraft.a}
                  onChange={(e) => setDerivedDraft(d => ({ ...d, a: e.target.value }))}
                >
                  <option value="">Series A...</option>
                  {selectedTickers.map(t => <option key={t} value={t}>{getTickerLabel(t)}</option>)}
                </select>
                {derivedDraft.op !== 'scale' && (
                  <select
                    className="market-data-transform-select"
                    value={derivedDraft.b}
                    onChange={(e) => setDerivedDraft(d => ({ ...d, b: e.target.value }))}
                  >
                    <option value="">Series B...</option>
                    {selectedTickers.map(t => <option key={t} value={t}>{getTickerLabel(t)}</option>)}
                  </select>
                )}
                {derivedDraft.op === 'scale' && (
                  <input
                    className="market-data-window-input"
                    type="number"
                    value={derivedDraft.k}
                    onChange={(e) => setDerivedDraft(d => ({ ...d, k: e.target.value }))}
                    title="Multiplier k"
                  />
                )}
                {derivedDraft.op === 'corr' && (
                  <input
                    className="market-data-window-input"
                    type="number"
                    min="2"
                    value={derivedDraft.window}
                    onChange={(e) => setDerivedDraft(d => ({ ...d, window: e.target.value }))}
                    title="Correlation window"
                  />
                )}
                <button
                  className="market-data-axis-btn"
                  onClick={addDerivedSeries}
                  disabled={!derivedDraft.a || (derivedDraft.op !== 'scale' && !derivedDraft.b)}
                >Add</button>
              </div>
            )}
          </div>
        )}

        {hasChartData && chartJsData && (
          <div className="backtest-chart-card" style={{ width: '100%' }}>
            <h3>Market Data</h3>

            {/* Row 1: chart mode + mode-specific controls */}
            <div className="market-data-chart-toolbar">
              <div className="market-data-toolbar-group">
                <span>Chart:</span>
                {CHART_MODES.map(m => (
                  <button
                    key={m.value}
                    className={`market-data-axis-btn${chartMode === m.value ? ' active' : ''}`}
                    onClick={() => setChartMode(m.value)}
                  >{m.label}</button>
                ))}
              </div>
              {chartMode === 'timeseries' && (
                <div className="market-data-toolbar-group">
                  <button
                    className={`market-data-axis-btn${stacked ? ' active' : ''}`}
                    onClick={() => {
                      const turningOn = !stacked;
                      setStacked(turningOn);
                      // One-click stacked bar: if nothing is a bar/area yet,
                      // stacking lines is a no-op, so convert all series.
                      if (turningOn && !anyBars) applyToAll('type', 'bar');
                    }}
                    title="Stack bar/area series (converts series to bars if none are yet)"
                  >Stacked</button>
                </div>
              )}
              {chartMode === 'histogram' && (
                <div className="market-data-toolbar-group">
                  <span>Series:</span>
                  {seriesSelect(histTicker, (e) => setHistTicker(e.target.value))}
                  <span>Bins:</span>
                  <input
                    className="market-data-window-input"
                    type="number" min="5" max="100"
                    value={histBins}
                    onChange={(e) => setHistBins(Math.max(5, Math.min(100, parseInt(e.target.value) || 30)))}
                  />
                </div>
              )}
              {chartMode === 'scatter' && (
                <div className="market-data-toolbar-group">
                  <span>X axis:</span>
                  {seriesSelect(scatterX, (e) => setScatterX(e.target.value))}
                  <button
                    className={`market-data-axis-btn${trendLine ? ' active' : ''}`}
                    onClick={() => setTrendLine(p => !p)}
                  >Trend line</button>
                  <button
                    className={`market-data-axis-btn${recencyFade ? ' active' : ''}`}
                    onClick={() => setRecencyFade(p => !p)}
                  >Fade by date</button>
                </div>
              )}
              {chartMode === 'seasonal' && (
                <div className="market-data-toolbar-group">
                  <span>Series:</span>
                  {seriesSelect(seasonalTicker, (e) => setSeasonalTicker(e.target.value))}
                  <span>Years:</span>
                  <input
                    className="market-data-window-input"
                    type="number" min="2" max="30"
                    value={seasonalYears}
                    onChange={(e) => setSeasonalYears(Math.max(2, Math.min(30, parseInt(e.target.value) || 5)))}
                  />
                </div>
              )}
            </div>

            {/* Row 2: date range + zoom + export/save */}
            <div className="market-data-chart-toolbar">
              {chartMode !== 'seasonal' && (
                <div className="market-data-toolbar-group">
                  <span>Range:</span>
                  {RANGE_PILLS.map(p => (
                    <button
                      key={p.value}
                      className={`market-data-axis-btn${rangeMode === p.value ? ' active' : ''}`}
                      onClick={() => setRangeMode(p.value)}
                    >{p.label}</button>
                  ))}
                  {rangeMode === 'fixed' && (
                    <>
                      <input
                        className="market-data-date-input"
                        type="date"
                        value={fixedStart}
                        onChange={(e) => setFixedStart(e.target.value)}
                        title="Start date"
                      />
                      <input
                        className="market-data-date-input"
                        type="date"
                        value={fixedEnd}
                        onChange={(e) => setFixedEnd(e.target.value)}
                        title="End date"
                      />
                    </>
                  )}
                  <button
                    className={`market-data-axis-btn${showZoom ? ' active' : ''}`}
                    onClick={() => setShowZoom(prev => !prev)}
                  >Zoom</button>
                  {showZoom && (
                    <button
                      className="market-data-axis-btn"
                      onClick={() => { setXStart(0); setXEnd(100); }}
                    >Reset</button>
                  )}
                </div>
              )}
              <div className="market-data-toolbar-group">
                <button
                  className="market-data-axis-btn"
                  onClick={handleExportChart}
                  title="Export chart as PNG (PowerPoint size)"
                >Export Image</button>
                <button
                  className="market-data-axis-btn"
                  onClick={handleCopyChart}
                  title="Copy chart PNG to the clipboard"
                >Copy</button>
                <button
                  className="market-data-axis-btn"
                  onClick={handleExportCsv}
                  title="Download the charted (transformed, date-ranged) series as CSV"
                >CSV</button>
                {savedChartsEnabled && (
                <button
                  className="market-data-axis-btn"
                  onClick={openSaveModal}
                  title="Save this chart configuration for reuse and deck building"
                >Save Chart</button>
                )}
                {savedChartsEnabled && loadedConfig?.can_edit && (
                  <button
                    className="market-data-axis-btn"
                    onClick={handleOverwriteLoaded}
                    disabled={savingConfig}
                    title={`Overwrite "${loadedConfig.name}" with the current chart`}
                  >{savingConfig ? 'Saving…' : 'Overwrite'}</button>
                )}
              </div>
            </div>

            {/* Row 3: axes + style */}
            <div className="market-data-chart-toolbar">
              {axisToolbarGroup('y', 'Left Axis')}
              {hasRightAxis && axisToolbarGroup('y1', 'Right Axis')}
              <div className="market-data-toolbar-group">
                <button
                  className={`market-data-axis-btn${showStylePanel ? ' active' : ''}`}
                  onClick={() => setShowStylePanel(p => !p)}
                >Style</button>
                <button
                  className={`market-data-axis-btn${showAnnotations ? ' active' : ''}`}
                  onClick={() => setShowAnnotations(p => !p)}
                >Annotations</button>
              </div>
            </div>

            {showStylePanel && (
              <div className="market-data-style-panel">
                <div className="market-data-toolbar-group">
                  <button
                    className={`market-data-axis-btn${gridlines ? ' active' : ''}`}
                    onClick={() => setGridlines(p => !p)}
                  >Gridlines</button>
                  <button
                    className={`market-data-axis-btn${zeroLine ? ' active' : ''}`}
                    onClick={() => setZeroLine(p => !p)}
                  >Zero line</button>
                  <button
                    className={`market-data-axis-btn${lastValues ? ' active' : ''}`}
                    onClick={() => setLastValues(p => !p)}
                  >Last values</button>
                  <span>Legend:</span>
                  <select
                    className="market-data-transform-select"
                    value={legendShow ? legendPosition : 'hidden'}
                    onChange={(e) => {
                      if (e.target.value === 'hidden') setLegendShow(false);
                      else { setLegendShow(true); setLegendPosition(e.target.value); }
                    }}
                  >
                    <option value="hidden">Hidden</option>
                    <option value="bottom">Bottom</option>
                    <option value="top">Top</option>
                    <option value="right">Right</option>
                  </select>
                </div>
                <div className="market-data-toolbar-group" style={{ flexWrap: 'wrap' }}>
                  <span>Source:</span>
                  <input
                    className="market-data-text-input"
                    type="text"
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value)}
                  />
                  <span>Footnote:</span>
                  <input
                    className="market-data-text-input"
                    type="text"
                    placeholder="Optional footnote above the source line"
                    value={footnote}
                    onChange={(e) => setFootnote(e.target.value)}
                  />
                </div>
              </div>
            )}

            {showAnnotations && (
              <div className="market-data-style-panel">
                <div className="market-data-toolbar-group">
                  <button
                    className={`market-data-axis-btn${recessionBands ? ' active' : ''}`}
                    onClick={() => setRecessionBands(p => !p)}
                    title="Shade NBER US recessions"
                  >US recessions</button>
                  <button
                    className="market-data-axis-btn"
                    onClick={() => setAnnBands(b => [...b, { start: '', end: '', label: '' }])}
                  >+ Band</button>
                  <button
                    className="market-data-axis-btn"
                    onClick={() => setAnnVlines(v => [...v, { date: '', label: '' }])}
                  >+ Event line</button>
                  <button
                    className="market-data-axis-btn"
                    onClick={() => setAnnHlines(h => [...h, { value: 0, axis: 'y', label: '', style: 'dashed' }])}
                  >+ Reference line</button>
                </div>
                {annBands.map((band, i) => (
                  <div key={`band-${i}`} className="market-data-toolbar-group">
                    <span>Band:</span>
                    <input className="market-data-date-input" type="date" value={band.start}
                      onChange={(e) => setAnnBands(bs => bs.map((b, j) => j === i ? { ...b, start: e.target.value } : b))} />
                    <input className="market-data-date-input" type="date" value={band.end}
                      onChange={(e) => setAnnBands(bs => bs.map((b, j) => j === i ? { ...b, end: e.target.value } : b))} />
                    <input className="market-data-text-input" style={{ width: 140 }} type="text" placeholder="Label" value={band.label || ''}
                      onChange={(e) => setAnnBands(bs => bs.map((b, j) => j === i ? { ...b, label: e.target.value } : b))} />
                    <button className="market-data-remove-btn" onClick={() => setAnnBands(bs => bs.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
                {annVlines.map((vline, i) => (
                  <div key={`vline-${i}`} className="market-data-toolbar-group">
                    <span>Event:</span>
                    <input className="market-data-date-input" type="date" value={vline.date}
                      onChange={(e) => setAnnVlines(vs => vs.map((v, j) => j === i ? { ...v, date: e.target.value } : v))} />
                    <input className="market-data-text-input" style={{ width: 140 }} type="text" placeholder="Label" value={vline.label || ''}
                      onChange={(e) => setAnnVlines(vs => vs.map((v, j) => j === i ? { ...v, label: e.target.value } : v))} />
                    <button className="market-data-remove-btn" onClick={() => setAnnVlines(vs => vs.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
                {annHlines.map((hline, i) => (
                  <div key={`hline-${i}`} className="market-data-toolbar-group">
                    <span>Reference:</span>
                    <input className="market-data-window-input" type="number" value={hline.value}
                      onChange={(e) => setAnnHlines(hs => hs.map((h, j) => j === i ? { ...h, value: Number(e.target.value) } : h))} />
                    <select className="market-data-transform-select" value={hline.axis || 'y'}
                      onChange={(e) => setAnnHlines(hs => hs.map((h, j) => j === i ? { ...h, axis: e.target.value } : h))}>
                      <option value="y">Left</option>
                      <option value="y1">Right</option>
                    </select>
                    <select className="market-data-transform-select" value={hline.style || 'dashed'}
                      onChange={(e) => setAnnHlines(hs => hs.map((h, j) => j === i ? { ...h, style: e.target.value } : h))}>
                      <option value="solid">Solid</option>
                      <option value="dashed">Dashed</option>
                      <option value="dotted">Dotted</option>
                    </select>
                    <input className="market-data-text-input" style={{ width: 140 }} type="text" placeholder="Label" value={hline.label || ''}
                      onChange={(e) => setAnnHlines(hs => hs.map((h, j) => j === i ? { ...h, label: e.target.value } : h))} />
                    <button className="market-data-remove-btn" onClick={() => setAnnHlines(hs => hs.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
              </div>
            )}

            {showZoom && chartMode !== 'seasonal' && (
              <div className="market-data-zoom-panel">
                <div className="market-data-zoom-row">
                  <label>Zoom (within range)</label>
                  <span className="market-data-zoom-label">Start</span>
                  <input type="range" min="0" max="100" value={xStart} onChange={(e) => { const v = Number(e.target.value); if (v < xEnd) setXStart(v); }} />
                  <span className="market-data-zoom-label">End</span>
                  <input type="range" min="0" max="100" value={xEnd} onChange={(e) => { const v = Number(e.target.value); if (v > xStart) setXEnd(v); }} />
                  <span className="market-data-zoom-value">{xStart}% – {xEnd}%</span>
                </div>
              </div>
            )}

            {mixedBarFrequencies && (
              <p style={{ fontSize: '0.78rem', color: '#6c757d', margin: '4px 0' }}>
                Bars work best when all series share a frequency — consider resampling daily bar series to monthly.
              </p>
            )}

            {chartJsData.error ? (
              <div className="alert alert-warning py-2 px-3" style={{ fontSize: '0.85rem' }}>
                {chartJsData.error}
              </div>
            ) : (
              <div style={{ height: 400 }}>
                <ChartCanvas
                  ref={chartRef}
                  type={chartMode === 'histogram' ? 'bar' : chartMode === 'scatter' ? 'scatter' : 'line'}
                  data={{ labels: chartJsData.labels, datasets: chartJsData.datasets }}
                  options={chartOptions}
                  plugins={[annotationsPlugin, sdBandsPlugin, zeroLinePlugin, lastValueLabelsPlugin]}
                />
              </div>
            )}
            <p className="market-data-chart-source">
              {footnote.trim() && <>{footnote.trim()}<br /></>}
              {sourceText}
            </p>
          </div>
        )}
      </div>

      {/* Save Chart Modal */}
      {showSaveModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(7,32,64,0.55)', zIndex: 9999 }} onClick={(e) => { if (e.target === e.currentTarget) setShowSaveModal(false); }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ background: 'var(--primary-color)', color: '#fff' }}>
                <h5 className="modal-title">Save Chart</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowSaveModal(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label small fw-bold">Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="e.g. US Equities vs Bond Yields"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Description (optional)</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    value={saveDescription}
                    onChange={(e) => setSaveDescription(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Chart title (optional, shown on rendered PNGs)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={chartTitle}
                    onChange={(e) => setChartTitle(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Chart subtitle (optional)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={chartSubtitle}
                    onChange={(e) => setChartSubtitle(e.target.value)}
                  />
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="saveChartFavSwitch"
                    checked={saveFavourite}
                    onChange={(e) => setSaveFavourite(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="saveChartFavSwitch">
                    Mark as favourite
                  </label>
                </div>
                <p className="text-muted small mt-3 mb-0">
                  Saves the series, chart type, transforms, axes, date range, styling and annotations. Trailing date ranges roll forward as new data arrives. Saved charts render server-side in the Drummond house style and can be assembled into PowerPoint decks.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setShowSaveModal(false)}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveSubmit}
                  disabled={savingConfig || !saveName.trim()}
                >
                  {savingConfig ? 'Saving…' : 'Save Chart'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer Card */}
      <div className="card disclaimer-card mt-3">
        <div className="card-body">
          <div className="disclaimer-text">
            <ManagedDisclaimer dkey="data_notice" as="p" className="small text-muted mb-0" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default MarketDataApp;
